import * as React from "react";

import { usePlaidLink } from "react-plaid-link";
import { useMutation } from "react-query";
import * as Redux from "react-redux";

import { MopButton } from "client/components";
import { getSetBankFormComplete } from "client/domain/FormBuilder/BankInformation";
import { useMerchantModel, useRestore } from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { CountryList, SalesChannel } from "client/lib/apt-merchant";
import { BankFieldSetNames } from "client/lib/apt-merchant/src/fieldsets";
import { BankService } from "client/services";
import { errorActions, formCompletionActions } from "client/store";

import { BankFieldSetLabel } from "./BankConnector";

type PlaidProps = {
  onSubmitSuccess?: () => void;
  token: string;
  registeredCountry?: CountryList;
  salesChannel?: SalesChannel;
  plaidBankAccountName: string;
  fieldSetName: BankFieldSetNames;
};

export const Plaid: React.FC<PlaidProps> = ({
  onSubmitSuccess,
  plaidBankAccountName,
  registeredCountry,
  salesChannel,
  token,
  fieldSetName,
}) => {
  const { setPrimaryBankVerificationSource } = useMerchantModel();
  const dispatch = Redux.useDispatch();
  const { refetchRestore } = useRestore();
  const { mutate: submitPlaid } = useMutation(BankService.updatePlaidInfo, {
    onError: (err: Error) => {
      dispatch(errorActions.setError(err.message));
    },
    onSuccess: ({ error, formCompletedState }) => {
      if (error?.plaidError) {
        dispatch(errorActions.setError(error.plaidError));
      } else {
        if (fieldSetName === "PrimaryBankFieldSet") {
          setPrimaryBankVerificationSource("PLAID");
        }

        // If we have the multi-step bank account info
        // where we want to collect primary and secondary
        // bank info we have to move to the next step instead
        // of dispatching to move to the next page
        if (onSubmitSuccess) {
          refetchRestore();
          onSubmitSuccess();
        } else {
          dispatch(formCompletionActions.setFormCompletion(formCompletedState));
        }
      }
    },
  });

  const onSuccess = React.useCallback(
    (token, metadata) => {
      const id = metadata?.account_id;
      const bankName = metadata.institution?.name;

      if (id) {
        logAmplitudeEvent("REDESIGN_PLAID", "SUCCESS", {
          link_session_id: metadata.link_session_id,
        });
      }

      const setBankFormComplete = getSetBankFormComplete({
        fieldSetName,
        salesChannel,
        registeredCountry,
      });

      const bankLabel: BankFieldSetLabel =
        fieldSetName === "PrimaryBankFieldSet" ? "PRIMARY" : "SECONDARY";

      if (id && bankName) {
        const request = {
          token,
          id,
          bankAccountName: plaidBankAccountName,
          registeredCountry,
          bankName,
          bankLabel,
          setBankFormComplete,
        };
        submitPlaid(request);
      }
    },
    [submitPlaid, registeredCountry, plaidBankAccountName]
  );

  const config = {
    token,
    onSuccess,
  };
  const { open, ready } = usePlaidLink(config);
  return (
    <MopButton
      onClick={() => open()}
      disabled={!ready}
      type="button"
      mopVariant="primary"
      fullWidth
    >
      <strong>Link bank</strong>
    </MopButton>
  );
};
